import { ReactNode } from 'react'
import { UserRole } from '../types'

/**
 * The settings sections, defined once and shared by the two places that render
 * them: the main sidebar's expanded Settings menu, and the settings page's own
 * small-screen tab strip. Keeping a single list is what stops the two menus
 * drifting apart as sections are added.
 */
export type SettingsTab = {
  id: string
  name: string
  group: string
  /** Roles allowed to see this section; omitted means every signed-in role. */
  roles?: UserRole[]
  icon: ReactNode
}

export const DEFAULT_SETTINGS_TAB = 'profile'

const SETTINGS_TABS: SettingsTab[] = [
  {
    id: 'profile',
    name: 'Profile',
    group: 'Your account',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
  },
  {
    id: 'payment-methods',
    name: 'Payment Methods',
    group: 'Your account',
    roles: [UserRole.BIDDER],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
  },
  {
    id: 'invoices',
    name: 'Invoices',
    group: 'Your account',
    roles: [UserRole.BIDDER],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  },
  {
    id: 'transactions',
    name: 'Transactions',
    group: 'Your account',
    roles: [UserRole.BIDDER],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
      </svg>
    ),
  },
  {
    id: 'notifications',
    name: 'Notifications',
    group: 'Your account',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM4.343 12.344l1.414 1.414L9 10.414V9a3 3 0 016 0v1.414l3.243 3.243 1.414-1.414L12 4.343z" />
      </svg>
    ),
  },
  {
    id: 'email-templates',
    name: 'Email Templates',
    group: 'Organisation',
    roles: [UserRole.AUCTIONEER],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    id: 'organization',
    name: 'Organization',
    group: 'Organisation',
    roles: [UserRole.AUCTIONEER, UserRole.STAFF],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
  },
  {
    id: 'domain',
    name: 'Domain',
    group: 'Organisation',
    roles: [UserRole.AUCTIONEER, UserRole.STAFF],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
      </svg>
    ),
  },
  {
    id: 'payment-gateway',
    name: 'Payment Gateway',
    group: 'Payments',
    roles: [UserRole.AUCTIONEER],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
  },
  {
    id: 'auction-defaults',
    name: 'Auction Defaults',
    group: 'Auctions',
    roles: [UserRole.AUCTIONEER],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
  },
  {
    id: 'tracking',
    name: 'Tracking',
    group: 'Auctions',
    roles: [UserRole.AUCTIONEER],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
  },
  {
    id: 'platform-fees',
    name: 'Platform Fees',
    group: 'Platform',
    roles: [UserRole.SYSTEM_ADMIN],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    id: 'stripe-mode',
    name: 'Stripe Mode',
    group: 'Platform',
    roles: [UserRole.SYSTEM_ADMIN],
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5M18 9l3-3m0 0l-3-3m3 3H10" />
      </svg>
    ),
  },
]

/**
 * Section order is fixed here rather than derived from the tabs, so the menu
 * reads the same for every role - a bidder simply sees fewer sections. A tab
 * with an unrecognised group still renders, under Other, so a new section can
 * never vanish from the menu by forgetting to list its group here.
 */
const GROUP_ORDER = ['Your account', 'Organisation', 'Auctions', 'Payments', 'Platform']

export type SettingsTabGroup = { group: string; items: SettingsTab[] }

/** Every section this role may open, flat and in menu order. */
export function getSettingsTabs(role?: UserRole): SettingsTab[] {
  return SETTINGS_TABS.filter(tab => !tab.roles || (role !== undefined && tab.roles.includes(role)))
}

/** The same sections, bucketed into the headed groups the menus render. */
export function getGroupedSettingsTabs(role?: UserRole): SettingsTabGroup[] {
  const tabs = getSettingsTabs(role)

  return [...GROUP_ORDER, 'Other']
    .map(group => ({
      group,
      items: tabs.filter(tab =>
        group === 'Other' ? !GROUP_ORDER.includes(tab.group) : tab.group === group
      ),
    }))
    .filter(section => section.items.length > 0)
}
